import React, { useState } from 'react';
import axios from 'axios';

const api = process.env.REACT_APP_API;

const UploadFinancialDataButton = ({ docId, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type (Excel, CSV)
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        'text/csv', // .csv
        'application/csv'
      ];
      
      const validExtensions = ['.xlsx', '.xls', '.csv'];
      const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (validTypes.includes(selectedFile.type) || validExtensions.includes(fileExtension)) {
        setFile(selectedFile);
        setErr('');
      } else {
        setErr('Please select an Excel (.xlsx, .xls) or CSV file containing financial data.');
        setFile(null);
      }
    }
  };

  const uploadFinancialData = async () => {
    if (!file || !docId) return;
    
    setBusy(true);
    setErr('');
    setResult(null);
    
    try {
      const formData = new FormData();
      formData.append('financialData', file);
      formData.append('docId', docId);
      formData.append('dataType', 'financial_ledger');
      formData.append('description', `Financial data for ${file.name}`);
      
      const res = await axios.post(
        `${api}/admin/rpa/docs/${docId}/financial/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true
        }
      );
      
      setResult(res.data || {});
      setOpen(true);
      if (typeof onDone === 'function') onDone(); // refresh the dashboard
    } catch (e) {
      console.error('[RPA] Financial data upload failed:', e);
      setErr(e.response?.data?.message || 'Upload failed. Please try again.');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const openModal = () => {
    setOpen(true);
    setFile(null);
    setResult(null);
    setErr('');
  };

  return (
    <span style={{ display: 'inline-block' }}>
      <button onClick={openModal} disabled={busy}>
        {busy ? 'Uploading…' : 'Upload Financial Data'}
      </button>

      {open && (
        <div style={modalOverlay}>
          <div style={modalPanel}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Upload Financial Data</h3>
              <button onClick={() => setOpen(false)} style={closeButton}>✕</button>
            </div>

            {!result ? (
              <div>
                <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
                  Upload an Excel or CSV file containing financial spending records, transactions, 
                  or ledger data for compliance analysis.
                </p>

                <div style={uploadArea}>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    style={{ marginBottom: 12 }}
                  />
                  
                  {file && (
                    <div style={fileInfo}>
                      <div style={{ fontWeight: 'bold', fontSize: 14 }}>{file.name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        Size: {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        Type: {file.type || 'Unknown'}
                      </div>
                    </div>
                  )}
                </div>

                {err && (
                  <div style={errorBox}>
                    {err}
                  </div>
                )}

                <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => setOpen(false)} style={secondaryButton}>
                    Cancel
                  </button>
                  <button 
                    onClick={uploadFinancialData} 
                    disabled={!file || busy}
                    style={primaryButton}
                  >
                    {busy ? 'Uploading...' : 'Upload Financial Data'}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {err ? (
                  <div style={errorBox}>
                    <strong>Upload Failed:</strong> {err}
                  </div>
                ) : (
                  <div>
                    <div style={successBox}>
                      <div style={{ fontWeight: 'bold', marginBottom: 8 }}>✅ Upload Successful</div>
                      <div><strong>File:</strong> {result.originalName || file?.name}</div>
                      <div><strong>Size:</strong> {result.size ? `${(result.size / 1024 / 1024).toFixed(2)} MB` : 'Unknown'}</div>
                      <div><strong>Rows:</strong> {result.rowCount?.toLocaleString() || 'Analyzing...'}</div>
                      {result.structure && (
                        <div style={{ marginTop: 8 }}>
                          <strong>Columns Detected:</strong>
                          <div style={{ fontSize: 12, marginTop: 4, padding: 8, background: '#f8f9fa', borderRadius: 4 }}>
                            {Array.isArray(result.structure) 
                              ? result.structure.join(', ')
                              : typeof result.structure === 'string' 
                                ? result.structure 
                                : JSON.stringify(result.structure)
                            }
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div style={{ marginTop: 12, fontSize: 13, color: '#059669' }}>
                      Financial data uploaded successfully. You can now proceed to ingest and analyze this data for compliance.
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <button onClick={() => setOpen(false)} style={primaryButton}>
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </span>
  );
};

// Styles
const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000
};

const modalPanel = {
  background: 'white',
  borderRadius: 8,
  padding: 24,
  maxWidth: 500,
  width: '90%',
  maxHeight: '80vh',
  overflow: 'auto',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
};

const uploadArea = {
  border: '2px dashed #d1d5db',
  borderRadius: 8,
  padding: 20,
  textAlign: 'center',
  background: '#f9fafb'
};

const fileInfo = {
  background: '#f0f9ff',
  border: '1px solid #0ea5e9',
  borderRadius: 6,
  padding: 12,
  marginTop: 8
};

const errorBox = {
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 6,
  padding: 12,
  color: '#dc2626',
  fontSize: 14
};

const successBox = {
  background: '#f0fdf4',
  border: '1px solid #22c55e',
  borderRadius: 6,
  padding: 12,
  color: '#15803d',
  fontSize: 14
};

const closeButton = {
  background: 'none',
  border: 'none',
  fontSize: 18,
  cursor: 'pointer',
  color: '#6b7280',
  padding: 4
};

const primaryButton = {
  background: '#3b82f6',
  color: 'white',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
  fontWeight: 500
};

const secondaryButton = {
  background: '#f3f4f6',
  color: '#374151',
  border: '1px solid #d1d5db',
  padding: '8px 16px',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer'
};

export default UploadFinancialDataButton;